/**
 * Общий кэш для ответов внешних сервисов.
 *
 * Два уровня: память живёт, пока открыта вкладка, и снимает повторные
 * обращения внутри сессии; localStorage переживает перезагрузку и снимает
 * их между заходами. Ходить в сеть за тем, что уже отвечали минуту назад —
 * невежливо по отношению к бесплатным API, на которых всё это держится.
 */
interface Entry<T> { at: number; data: T }

const mem = new Map<string, Entry<unknown>>();

function readLocal<T>(key: string, ttl: number): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const e = JSON.parse(raw) as Entry<T>;
    if (!e || typeof e.at !== "number" || Date.now() - e.at > ttl) return null;
    return e.data;
  } catch { return null; }
}

function writeLocal<T>(key: string, data: T) {
  try { localStorage.setItem(key, JSON.stringify({ at: Date.now(), data })); }
  catch {
    // Переполнение — выкидываем свои старые записи и пробуем ещё раз.
    try {
      for (const k of Object.keys(localStorage)) if (k.startsWith("pw.c:")) localStorage.removeItem(k);
      localStorage.setItem(key, JSON.stringify({ at: Date.now(), data }));
    } catch { /* приватный режим */ }
  }
}

/**
 * Отдать из кэша или сходить в сеть.
 * `persist: false` — только память: для того, что не стоит держать на диске.
 */
export async function cached<T>(
  key: string,
  ttlMs: number,
  fetcher: () => Promise<T>,
  opts: { persist?: boolean } = {},
): Promise<T> {
  const full = `pw.c:${key}`;
  const persist = opts.persist !== false;

  const m = mem.get(full) as Entry<T> | undefined;
  if (m && Date.now() - m.at <= ttlMs) return m.data;

  if (persist) {
    const l = readLocal<T>(full, ttlMs);
    if (l !== null) {
      mem.set(full, { at: Date.now(), data: l });
      return l;
    }
  }

  const data = await fetcher();
  mem.set(full, { at: Date.now(), data });
  if (persist) writeLocal(full, data);
  return data;
}

/** Координаты в ключ: сетка ~1 км, чтобы соседние точки били в один кэш. */
export const geoKey = (lat: number, lon: number) =>
  `${lat.toFixed(2)},${lon.toFixed(2)}`;
